import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

export function SubscriptionRequestPage() {
  const [selectedPackage, setSelectedPackage] = useState('');

  const { data: packagesData, isLoading } = useQuery({
    queryKey: ['subscription-packages'],
    queryFn: () => api.get('/api/subscription/request/lookup').then((r) => r.data),
  });

  const { data: packageDetails } = useQuery({
    queryKey: ['package-details', selectedPackage],
    queryFn: () =>
      api.get('/api/subscription/request/getPackageDetails', { params: { package_id: selectedPackage } }).then((r) => r.data),
    enabled: !!selectedPackage,
  });

  const requestMutation = useMutation({
    mutationFn: (packageId: string) =>
      api.post('/api/subscription/request/add', { package_id: packageId }),
    onSuccess: () => {
      toast.success('Credit request submitted successfully');
      setSelectedPackage('');
    },
    onError: () => toast.error('Failed to submit credit request'),
  });

  const packages = packagesData?.query?.data ?? [];

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/subscription">
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Request Credits</h1>
          <p className="text-gray-500">Select a package and submit a credit request</p>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading packages...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Package List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Available Packages</CardTitle>
            </CardHeader>
            <CardContent>
              {packages.length === 0 ? (
                <p className="text-gray-500">No packages available.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Package</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {packages.map((pkg: any) => (
                      <TableRow key={pkg.package_id || pkg.id}>
                        <TableCell className="font-medium">{pkg.package_name || pkg.name}</TableCell>
                        <TableCell>{pkg.credit_amount || pkg.credits}</TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={selectedPackage === (pkg.package_id || pkg.id) ? 'default' : 'outline'}
                            onClick={() => setSelectedPackage(pkg.package_id || pkg.id)}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Package Details & Request */}
          <div>
            {selectedPackage && packageDetails ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Package Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <span className="text-gray-500">Name:</span>
                      <span className="font-medium">{packageDetails.package_name || packageDetails.name}</span>
                      <span className="text-gray-500">Credits:</span>
                      <span className="font-medium">{packageDetails.credit_amount || packageDetails.credits}</span>
                      <span className="text-gray-500">Price:</span>
                      <span className="font-medium">{packageDetails.price ? `NZD ${packageDetails.price}` : '—'}</span>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => requestMutation.mutate(selectedPackage)}
                    disabled={requestMutation.isPending}
                  >
                    {requestMutation.isPending ? 'Submitting...' : 'Submit Credit Request'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  Select a package to view details
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}